import { Check, Circle, Plane } from 'lucide-react';

import type { ActivationPhase } from '../hooks/useDeviceCodeActivation';
import { ACCENT, STATE, STROKE, TEXT } from '../theme/tokens';

// FD-493 — the three stages of a wave activation, inside the dialog that runs
// them: Authenticate → Mint credential → Complete (mockups 5–7).
//
// ── WHAT THIS DELIBERATELY DOES NOT RENDER, AND WHY THAT IS THE POINT ──────
//
// Mockup 6 draws FIVE sub-stages behind a 67% progress bar: "Authenticating
// with Microsoft Graph", "Validating permissions", "Creating enrollment token",
// "Storing credential", "Finalizing". None of them is observable from this
// browser. `activate-wave` is ONE HTTP request that checks out the licence,
// runs the DRS poll, writes the secret to Key Vault, stamps the migration row
// and updates the tenant registry, and then returns — the client sees a request
// in flight and then an answer. A bar advancing through five named steps would
// be the console narrating work it cannot see, which is exactly the class of
// fiction `FD-ADR-043` D4 refuses ("↑ 12%" deltas, activity feeds,
// deployment-wide donuts: omitted rather than invented).
//
// So there is NO PERCENTAGE ANYWHERE in this control, and there are exactly
// three stages — the ones `useDeviceCodeActivation` actually distinguishes.
// `ACTIVATING_HINT_MS` is what promotes stage 1 to stage 2: an attempt that has
// outrun a pending check IS the mint, and that is a real signal rather than a
// timer dressed up as one.
//
// ── ONE REFUSAL AND ONE RECOVERY LOOK THE SAME HERE, CORRECTLY ─────────────
//
// `expired` and `failed` both put the stepper back on stage 1 with nothing
// done, because in both cases NOTHING WAS MINTED — the dialog's own copy says
// so ("nothing was minted and there is nothing to clean up"). A stepper that
// left stage 2 marked half-complete after a failure would imply a credential
// exists somewhere, which is the one thing an operator must not be left
// wondering about a token Rubix cannot revoke.

export type ActivationStage = 'authenticate' | 'mint' | 'complete';

const ORDER: ActivationStage[] = ['authenticate', 'mint', 'complete'];

/**
 * Three captions per stage, not two.
 *
 * A stage that is CURRENT is neither "about to happen" nor "has happened", and
 * the difference is load-bearing at the end of the run: reaching stage three IS
 * the completion, so a two-caption control would leave a finished activation
 * reading the caption of a step that had not started. Mockup 5 draws stage three
 * pending as "Credential created" — a claim about a credential that does not
 * exist yet — so the pending wording states the stage's OUTCOME instead.
 */
const COPY: Record<
  ActivationStage,
  { label: string; pending: string; current: string; done: string }
> = {
  authenticate: {
    label: 'Authenticate',
    pending: 'Sign in to destination tenant',
    current: 'Sign in to destination tenant',
    done: 'Signed in to destination tenant',
  },
  mint: {
    label: 'Mint credential',
    pending: 'Create BPRT token',
    current: 'Creating the BPRT token',
    done: 'BPRT created successfully',
  },
  complete: {
    label: 'Complete',
    pending: 'Credential ready',
    current: 'Credential ready',
    done: 'Credential ready',
  },
};

/**
 * Which stage the activation is on, from the facts the dialog already holds.
 *
 * Pure and exported so it is asserted against every `ActivationPhase` kind
 * rather than inferred from whatever the dialog happened to render — the
 * `campaignLifecycle` discipline at a smaller scale.
 *
 * `pasteBusy` is what makes the PASTED path honest. It has no device-code
 * sign-in at all: the operator has already authenticated elsewhere, so stage 1
 * is done the moment they submit, and the single in-flight `activate-wave` call
 * is the mint. Without this the paste path would sit on "Authenticate" through
 * the one request that does the work.
 */
export function activationStage(
  phase: ActivationPhase,
  opts: { pasteBusy?: boolean; activated?: boolean } = {},
): ActivationStage {
  if (opts.activated || phase.kind === 'done') return 'complete';
  if (opts.pasteBusy || phase.kind === 'activating') return 'mint';
  // `idle`, `starting`, `awaiting-signin`, `expired` and `failed` are all
  // "no credential exists yet" — see the header note on why the last two are
  // not left part-way through.
  return 'authenticate';
}

const ICON_SIZE = 13;

export default function ActivationStepper({ stage }: { stage: ActivationStage }) {
  const currentIndex = ORDER.indexOf(stage);

  return (
    <ol
      className="m-0 flex list-none flex-col gap-3 p-0 sm:flex-row sm:gap-0"
      aria-label="Activation progress"
    >
      {ORDER.map((id, index) => {
        const done = index < currentIndex;
        const current = index === currentIndex;
        const isLast = index === ORDER.length - 1;

        // FD-467's rule, inherited: colour alone is not a state a screen reader
        // can read, so the stage carries `aria-current="step"` and each caption
        // is real text rather than a tooltip.
        const circleBg = current ? ACCENT.base : done ? STATE.success.glyph : STROKE.base;
        const circleFg = !current && !done ? TEXT.secondary : '#FFFFFF';

        return (
          <li
            key={id}
            className="flex min-w-0 flex-1 items-start gap-3 sm:flex-col sm:items-center sm:gap-0"
            aria-current={current ? 'step' : undefined}
          >
            <div className="flex shrink-0 items-center sm:w-full">
              {/* Connectors exist only in the horizontal layout — stacked, they
                  would be three dangling stubs down the left margin. */}
              <span
                aria-hidden="true"
                className="hidden h-[2px] flex-1 sm:block"
                style={{
                  backgroundColor:
                    index === 0
                      ? 'transparent'
                      : index - 1 < currentIndex
                        ? STATE.success.glyph
                        : STROKE.base,
                }}
              />
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: circleBg, color: circleFg }}
              >
                {/* strokeWidth 2, not the console's 1.5, and for the reason
                    `CampaignStepper` and `PhaseStepper` both give: a 1.5 glyph
                    inside a filled disc this small reads as a smudge. One weight
                    across all three steppers beats a rule applied to a control it
                    was not written about — and every icon in THIS view carries it. */}
                {done ? (
                  <Check size={ICON_SIZE} strokeWidth={2} />
                ) : current ? (
                  <Plane size={ICON_SIZE} strokeWidth={2} />
                ) : (
                  <Circle size={6} strokeWidth={3} />
                )}
              </span>
              <span
                aria-hidden="true"
                className="hidden h-[2px] flex-1 sm:block"
                style={{
                  backgroundColor: isLast
                    ? 'transparent'
                    : done
                      ? STATE.success.glyph
                      : STROKE.base,
                }}
              />
            </div>

            <div className="min-w-0 sm:mt-1.5 sm:px-2 sm:text-center">
              <div
                className="text-[12px]"
                style={{ color: TEXT.primary, fontWeight: current ? 600 : 400 }}
              >
                {COPY[id].label}
              </div>
              <div className="mt-0.5 text-[10px] leading-snug" style={{ color: TEXT.secondary }}>
                {done ? COPY[id].done : current ? COPY[id].current : COPY[id].pending}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
